/**
 * Auth gate — wraps any route that should only render when the API
 * confirms the session cookie is valid.
 *
 * Behaviour:
 *   - While `useMe()` is in flight, render a spinner (full-bleed) so the
 *     screen doesn't flash content the user won't be allowed to see.
 *   - When `useMe()` errors with HTTP 401 (`UNAUTHENTICATED`), navigate
 *     the operator to `/login`. Any other error is surfaced as a
 *     fail-stop banner so a transient backend issue isn't silently
 *     papered over by sending the user to a login screen they don't
 *     need.
 *   - When `useMe()` succeeds, render the children.
 *
 * Implementation detail: `<Navigate>` is used (not the imperative
 * `router.navigate`) so the redirect itself is part of the React tree
 * and well-behaved during Suspense.
 */

import type { ReactElement, ReactNode } from "react";
import { Box, Alert, CircularProgress, Container, Stack, Typography } from "@mui/material";
import { Navigate } from "@tanstack/react-router";
import { ApiClientError } from "../lib/api";
import { useMe } from "../hooks/useAuth";

type Props = { children: ReactNode };

export function RequireAuth({ children }: Props): ReactElement {
  const me = useMe();

  if (me.isPending) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress aria-label="Checking session" />
      </Box>
    );
  }

  if (me.isError) {
    if (me.error instanceof ApiClientError && me.error.status === 401) {
      return <Navigate to="/login" />;
    }
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Stack spacing={2}>
          <Typography variant="h2">Something went wrong</Typography>
          <Alert severity="error">
            {me.error instanceof Error ? me.error.message : "Unable to verify session."}
          </Alert>
        </Stack>
      </Container>
    );
  }

  return <>{children}</>;
}
